/* eslint-disable react/self-closing-comp */
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import DialogComponent from '../dialog/dialog.component';
import { useAvatar } from '../../hooks/user-avatar/useAvatar';
import ButtonPrimary from '../button-refactor/button-primary';
import ButtonSecondary from '../button-refactor/button-secondary';
import { userService } from '../../services/user.service';
import toast from 'react-hot-toast';
import { Tooltip } from 'antd';
import { useCosmetic } from '../cosmetics/cosmetics-store';
import { frameRarityOf, RARITY_LABEL_VI } from '../cosmetics/cosmetic-visuals';
import AvatarFrame from '../cosmetics/avatar-frame';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

interface AvatarProps {
  avatar: string;
  label: string;
  size?: AvatarSize;
  uploaded?: boolean;
  preview?: boolean;
  onUploadSuccess?: () => void;
  classNameLabel?: string;
  showFullLabel?: boolean;
  onUploadAvatar?: any;
  customUpload?: string;
  /** userId của chủ avatar — dùng để vẽ hiệu ứng cosmetic đang gắn (nếu có). */
  userId?: string;
}

const Avatar = ({
  avatar,
  label,
  size = 'md',
  uploaded,
  onUploadSuccess,
  classNameLabel,
  showFullLabel,
  preview,
  customUpload,
  onUploadAvatar,
  userId,
}: AvatarProps) => {
  const { onInputChange, imageReviewSrc, visibleUpload, onClose } = useAvatar();
  const cosmetic = useCosmetic(userId);
  const frameRarity = frameRarityOf(cosmetic?.avatarEffect);
  // Số thứ tự phát hành Thần Thoại (01..12) hiện dưới avatar.
  const mint = frameRarity === 'mythic' ? cosmetic?.avatarEffect?.mintNumber : null;

  const fileRef = useRef<HTMLInputElement>(null);

  const [selfAvatar, setSelfAvatar] = useState<string>(avatar);
  const [isError, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [visiblePreview, setVisiblePreview] = useState<boolean>(false);

  const displayUserName = (): string => {
    if (showFullLabel) {
      return label;
    }

    if (!label) {
      return 'N/A';
    }

    const splitUserName = label.split(' ');
    if (splitUserName.length === 1) {
      return label[0];
    }

    const firstName = label[0];
    const lastName = splitUserName[splitUserName.length - 1][0];

    return firstName + lastName;
  };

  const handleUploadAvatar = async () => {
    const avatarFile = fileRef?.current?.files;
    if (!avatarFile) {
      return;
    }

    if (onUploadAvatar) {
      onUploadAvatar(avatarFile);
      onClose();
      return;
    }

    // Hiện ảnh NGAY từ file local (không chờ 5-6s upload + tải lại từ server).
    const localPreview = URL.createObjectURL(avatarFile[0]);
    setSelfAvatar(localPreview);
    onClose();
    try {
      setLoading(true);
      const data = await userService.updateAvatar(avatarFile[0]);
      setSelfAvatar(data[0].url); // thay bằng URL thật khi upload xong
      onUploadSuccess?.();
    } catch (error) {
      toast.error('Không thể cập nhật ảnh đại diện!');
    } finally {
      setLoading(false);
    }
  };

  const handleCLick = () => {
    if (preview) {
      setVisiblePreview(true);
    }
  };

  const avatarSize: Record<AvatarSize, string> = {
    xs: 'w-8 h-8',
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
    xl: 'w-24 h-24',
    xxl: 'w-40 h-40',
  };
  // px tương ứng để khung avatar tự scale (hạt chỉ bật cho size lớn — xử lý trong AvatarFrame)
  const avatarPx: Record<AvatarSize, number> = {
    xs: 32, sm: 40, md: 64, lg: 80, xl: 96, xxl: 160,
  };

  useEffect(() => {
    setSelfAvatar(avatar);
  }, [avatar]);

  const avatarBox = (
      <div
        className={classNames(`${avatarSize[size]} relative  z-10`, {
          'cursor-pointer hover:before:bg-slate-50 hover:before:bg-opacity-50 dark:hover:before:bg-opacity-20':
            uploaded,
          'cursor-pointer': preview,
        })}
        onClick={handleCLick}
      >
        {!isError ? (
          <div
            className={`${classNames({
              'overflow-hidden md:border-[2px] border-gray-100 dark:border-slate-900 shadow':
                size === 'xxl',
            })} relative z-[10] h-full w-full rounded-full shrink-0`}
            onClick={() => uploaded && setVisiblePreview(true)}
          >
            <img
              className={` ${'test'} h-full w-full rounded-full object-cover`}
              src={
                selfAvatar || '/default-avatar.svg'
              }
              alt="avatar"
              onError={() => {
                setError(true);
              }}
            />
          </div>
        ) : null}

        {isError ? (
          <div
            className={classNames({
              'relative overflow-hidden rounded-full md:border-[6px] border-gray-100 shrink-0 dark:border-slate-900 shadow':
                size === 'xxl',
            })}
            onClick={() => uploaded && setVisiblePreview(true)}
          >
            <img
              className={`avatar   rounded-full ${avatarSize[size]}`}
              src="/default-avatar.svg"
              alt="avatar"
            />
          </div>
        ) : null}

        {frameRarity ? (
          <AvatarFrame rarity={frameRarity} sizePx={avatarPx[size]} withParticles />
        ) : null}

        {mint && size !== 'sm' && size !== 'xs' ? (
          <span
            title={`Thần Thoại độc bản #${String(mint).padStart(2, '0')}`}
            className="absolute left-1/2 -bottom-1 z-20 -translate-x-1/2 rounded-full bg-gradient-to-r from-amber-400 to-fuchsia-500 px-1.5 text-[10px] font-bold leading-4 text-white shadow"
          >
            #{String(mint).padStart(2, '0')}
          </span>
        ) : null}

        {/* Nút đổi ảnh đã CHUYỂN vào dialog "Xem ảnh đại diện" (bấm avatar để xem) — bỏ nút ngoài để
            không che khung. Chỉ giữ input file ẩn để dialog kích hoạt. */}
        {uploaded ? (
          <input
            hidden
            type="file"
            ref={fileRef}
            onChange={onInputChange}
            accept="image/*"
          />
        ) : null}
      </div>
  );

  return (
    <>
      {frameRarity ? (
        <Tooltip title={`Huy hiệu ${RARITY_LABEL_VI[frameRarity]}`}>{avatarBox}</Tooltip>
      ) : (
        avatarBox
      )}

      <DialogComponent
        visible={visibleUpload}
        onClose={onClose}
        placement="CENTER"
        title="Đổi ảnh đại diện"
        size="lg"
      >
        <div className="pt-6">
          <img
            src={imageReviewSrc}
            alt=""
            className="mx-auto h-64 object-cover"
          />

          <div className="p-6 border-t border-t-[#DEE0E6] dark:border-t-slate-700 flex gap-2 justify-end items-center">
            <ButtonSecondary
              className="dark:text-slate-50 h-10 px-4 py-2 rounded-lg border border-solid !border-[#DEE0E6] dark:border-slate-700 text-base text-[#18191E] cursor-pointer font-medium"
              onClick={onClose}
            >
              Hủy bỏ
            </ButtonSecondary>
            <ButtonPrimary
              className="h-10 px-4 py-2 text-base bg-[#0071A6]"
              onClick={() => handleUploadAvatar()}
              loading={loading}
            >
              Cập nhật
            </ButtonPrimary>
          </div>
        </div>
      </DialogComponent>

      <DialogComponent
        visible={visiblePreview}
        onClose={() => setVisiblePreview(false)}
        placement="CENTER"
        title="Xem ảnh đại diện"
        size="lg"
      >
        <div className="p-6">
          <img
            src={selfAvatar || '/default-avatar.svg'}
            alt=""
            className="mx-auto h-96"
          />
        </div>
        <div className="mt-10 flex justify-end items-center gap-2 p-6 border border-t-[#DEE0E6] dark:border-t-slate-700">
          {uploaded ? (
            <ButtonSecondary
              className={`dark:text-slate-50 h-10 px-4 py-2 rounded-lg border border-solid !border-[#DEE0E6] dark:border-slate-700 text-base text-[#18191E] cursor-pointer font-medium ${customUpload}`}
              onClick={() => {
                setVisiblePreview(false);
                fileRef.current?.click();
              }}
            >
              Đổi ảnh đại diện
            </ButtonSecondary>
          ) : null}
          <ButtonPrimary
            onClick={() => setVisiblePreview(false)}
            loading={loading}
          >
            Đóng
          </ButtonPrimary>
        </div>
      </DialogComponent>
    </>
  );
};

export default Avatar;
